import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { briefingsService, Briefing, BriefingBloco, BriefingPendencia } from '../services/api';
import { BRIEFING_EXEMPLOS, ExemploCampo } from './briefingExemplos';
import { mensagemDoErroDeCarga, falhaIgnoradaDeProposito, campoComEstado } from '../design';

/**
 * Briefing de proposta do lead, nas três etapas do núcleo de propostas:
 * Etapa 1 (vendedor, logo depois da conversa), Etapa 2 (técnica e comercial),
 * Etapa 3 (pós-envio). O que falta nas etapas 1 e 2 é PENDÊNCIA, calculada pelo
 * backend, e é o que trava a geração da proposta. Nunca chutar: campo vazio é
 * honesto, chute vira mentira impressa.
 */

interface Props {
  leadId: number;
  leadData?: any;
  canEdit?: boolean;
  /**
   * Entrega ao pai uma função que grava o que ainda não foi salvo. O modal do
   * lead tem o próprio botão Salvar no rodapé, e quem preenche o briefing
   * clica nele: sem isto, o briefing inteiro ia embora em silêncio quando o
   * lead era salvo (relatado em 05/09/2026, briefing da SOS perdido).
   */
  registerFlush?: (fn: (() => Promise<void>) | null) => void;
  /**
   * Avisa o pai a cada gravação, com o briefing já recalculado pelo backend
   * (pendências e prontoParaGerar). Quem precisa disso é o "Gerar com IA", onde
   * o briefing passou a ser preenchido na MESMA tela que tem o botão: o botão é
   * liberado por readiness, e sem este aviso ele ficaria travado com o motivo
   * velho justamente depois de o vendedor preencher o campo que faltava.
   */
  onSaved?: (briefing: Briefing) => void;
  /**
   * A Etapa 3 é pós-envio: não trava geração nenhuma. Quem embute este editor
   * dentro do workspace de geração já tem a etapa Resultado na própria barra de
   * etapas, e mostrar o mesmo formulário duas vezes na mesma caixa faz o
   * vendedor preencher num lugar e duvidar do outro.
   */
  mostrarEtapa3?: boolean;
}

type Blocos = Record<BriefingBloco, Record<string, any>>;

const PORTAS: Array<{ v: string; l: string; d: string }> = [
  { v: 'whatsapp_first', l: 'WhatsApp-first', d: 'bloqueio Meta, WhatsApp pessoal, histórico perdido' },
  { v: 'voz_first', l: 'Voz-first', d: 'PABX que cai, custo de telefonia, central ou portaria' },
  { v: 'combo', l: 'Combo', d: 'as duas dores com peso' },
  { v: 'setor_publico', l: 'Setor público', d: 'regime de edital' },
];

const INPUT = 'w-full px-3 py-2 rounded-lg text-sm focus:outline-none placeholder:text-slate-600';
const PEND = 'border-amber-500/60';

interface FieldViewProps { label: string; value: any; onChange: (v: any) => void; pend: boolean; disabled: boolean; hint?: string; textarea?: boolean; type?: string; placeholder?: string; exemplos?: ExemploCampo[] }
function FieldView({ label, value, onChange, pend, disabled, hint, textarea, type = 'text', placeholder, exemplos }: FieldViewProps) {
  const cls = `${INPUT} ${campoComEstado(pend && PEND)}`;
  const [verExemplos, setVerExemplos] = useState(false);
  // Guarda o que estava escrito antes de aplicar um exemplo: clicar por engano
  // num campo já preenchido não pode apagar o que o vendedor digitou.
  const [antes, setAntes] = useState<string | null>(null);
  const temExemplos = !disabled && !!exemplos?.length;
  const aplicar = (texto: string) => { setAntes(String(value ?? '')); onChange(texto); };
  return (
    <div>
      <label className='flex items-center justify-between gap-2 text-xs text-slate-400 mb-1'>
        <span>
          {label}
          {pend && <span className='ml-2 text-[10px] font-mono uppercase tracking-wide text-amber-400'>a confirmar</span>}
        </span>
        {temExemplos && (
          <button type='button' onClick={() => setVerExemplos(v => !v)}
            className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${verExemplos ? 'bg-orange-500/15 border-orange-500/50 text-orange-300' : 'border-slate-700 text-slate-500 hover:text-slate-300 hover:border-slate-500'}`}>
            {verExemplos ? 'fechar' : `exemplos (${exemplos!.length})`}
          </button>
        )}
      </label>
      {textarea ? (
        <textarea rows={2} value={value ?? ''} disabled={disabled} placeholder={placeholder} onChange={e => onChange(e.target.value)} className={cls} />
      ) : (
        <input type={type} value={value ?? ''} disabled={disabled} placeholder={placeholder}
          onChange={e => onChange(type === 'number' ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value)} className={cls} />
      )}
      {hint && <p className='text-[11px] text-slate-500 mt-1'>{hint}</p>}
      {temExemplos && verExemplos && (
        <div className='mt-2 space-y-1.5 rounded-lg border border-slate-700 bg-slate-900/60 p-2'>
          <p className='text-[11px] text-slate-500'>De briefings reais. Clique para escrever no campo e edite com os dados deste cliente.</p>
          {exemplos!.map((ex, i) => (
            <button key={i} type='button' onClick={() => aplicar(ex.texto)}
              className='w-full text-left rounded-lg border border-slate-700 bg-slate-800/70 px-2.5 py-2 hover:border-orange-500/60 hover:bg-slate-800 transition-colors'>
              <span className='text-[10px] font-mono uppercase tracking-wide text-orange-400/80'>{ex.tag}</span>
              <span className='block text-xs text-slate-300 leading-snug mt-0.5'>{ex.texto}</span>
            </button>
          ))}
          {antes !== null && (
            <button type='button' onClick={() => { onChange(antes); setAntes(null); }}
              className='text-[11px] text-slate-400 underline underline-offset-2 hover:text-white'>
              desfazer e voltar ao que estava escrito
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface TriViewProps { label: string; value: boolean | undefined; onChange: (v: boolean | undefined) => void; pend: boolean; disabled: boolean; hint?: string }
function TriView({ label, value, onChange, pend, disabled, hint }: TriViewProps) {
  const btn = (val: boolean | undefined, txt: string) => (
    <button type='button' disabled={disabled} onClick={() => onChange(val)}
      className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${value === val ? 'bg-orange-500/15 border-orange-500/60 text-orange-300' : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white'}`}>{txt}</button>
  );
  return (
    <div>
      <label className='block text-xs text-slate-400 mb-1'>
        {label}
        {pend && <span className='ml-2 text-[10px] font-mono uppercase tracking-wide text-amber-400'>a confirmar</span>}
      </label>
      <div className='flex gap-2'>{btn(true, 'Sim')}{btn(false, 'Não')}{btn(undefined, 'Não sei')}</div>
      {hint && <p className='text-[11px] text-slate-500 mt-1'>{hint}</p>}
    </div>
  );
}

interface EtapaViewProps { n: 1 | 2 | 3; titulo: string; sub: string; aberta: boolean; onOpen: () => void; pendentes: number | null; completa?: boolean; children: React.ReactNode; innerRef?: (el: HTMLElement | null) => void }
function EtapaView({ n, titulo, sub, aberta, onOpen, pendentes, completa, children, innerRef }: EtapaViewProps) {
  return (
    <section ref={innerRef} className='bg-slate-800/50 border border-slate-700 rounded-xl scroll-mt-2'>
      <button type='button' onClick={onOpen} className='w-full flex items-center justify-between gap-3 p-4 text-left'>
        <div className='flex items-center gap-3 min-w-0'>
          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${completa ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-300'}`}>{completa ? '✓' : n}</span>
          <div className='min-w-0'>
            <p className='text-sm font-semibold text-white truncate'>{titulo}</p>
            <p className='text-xs text-slate-500 truncate'>{sub}</p>
          </div>
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          {pendentes !== null && (pendentes > 0
            ? <span className='text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/40'>{pendentes} a confirmar</span>
            : <span className='text-[11px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/40'>completa</span>)}
          <span className='text-slate-500 text-xs'>{aberta ? '▲' : '▼'}</span>
        </div>
      </button>
      {aberta && <div className='px-4 pb-4 space-y-4 border-t border-slate-700/60 pt-4'>{children}</div>}
    </section>
  );
}

export default function LeadBriefingTab({ leadId, leadData, canEdit = true, registerFlush, onSaved, mostrarEtapa3 = true }: Props) {
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [blocos, setBlocos] = useState<Blocos>({ descoberta: {}, oferta: {}, dimensionamento: {}, provas: {}, condicoes: {}, resultado: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // ⛔ FALHA FECHADA (07/10/2026). Se a leitura do briefing falhava, o formulário aparecia editável e VAZIO; um campo
  // digitado disparava o autosave com os seis blocos quase vazios, e o backend substitui cada bloco enviado inteiro
  // (proposal-briefings.service → upsert): o briefing real era sobrescrito. Fechar o modal gravava pelo flush. Agora,
  // sem uma leitura boa DESTE lead, o formulário não aparece; sem formulário nada fica sujo, e autosave, flush e o
  // gravar ao desmontar não têm o que gravar. A leitura é por lead: trocar de lead fecha o formulário antes de ler, e
  // a falha do lead novo não mostra o briefing do anterior sob ele.
  const [carregou, setCarregou] = useState(false);
  const leadCarregadoRef = useRef<number | null>(null);
  const [aberta, setAberta] = useState<1 | 2 | 3>(1);
  // O flush é chamado de fora, fora do ciclo de render: precisa ler o estado
  // mais recente por ref, não pela closure do último render.
  const blocosRef = useRef<Blocos>(blocos);
  const dirtyRef = useRef(false);
  const secoesRef = useRef<Record<number, HTMLElement | null>>({});
  const autosaveRef = useRef<number | null>(null);
  const jaAbriuRef = useRef(false);
  // Por ref para que `gravar` não mude de identidade quando o pai passa arrow
  // inline — `gravar` é o que o autosave e o flush registrado carregam.
  const onSavedRef = useRef(onSaved);
  useEffect(() => { onSavedRef.current = onSaved; }, [onSaved]);

  const load = useCallback(async () => {
    setLoading(true); setErro(null);
    if (leadCarregadoRef.current !== leadId) { leadCarregadoRef.current = null; setCarregou(false); }
    try {
      const b = await briefingsService.get(leadId);
      setBriefing(b);
      setBlocos({
        descoberta: { nomeFantasia: leadData?.tradeName || leadData?.companyName || '', ...(b.descoberta || {}) },
        oferta: b.oferta || {},
        dimensionamento: b.dimensionamento || {},
        provas: b.provas || {},
        condicoes: { validadeDias: 30, ...(b.condicoes || {}) },
        resultado: b.resultado || {},
      });
      blocosRef.current = {
        descoberta: { nomeFantasia: leadData?.tradeName || leadData?.companyName || '', ...(b.descoberta || {}) },
        oferta: b.oferta || {},
        dimensionamento: b.dimensionamento || {},
        provas: b.provas || {},
        condicoes: { validadeDias: 30, ...(b.condicoes || {}) },
        resultado: b.resultado || {},
      };
      dirtyRef.current = false;
      setDirty(false);
      leadCarregadoRef.current = leadId;
      setCarregou(true);
    } catch (e: any) {
      if (leadCarregadoRef.current !== leadId) setErro(`Não consegui carregar o briefing: ${mensagemDoErroDeCarga(e)}`);
      else setErro(`Não consegui recarregar o briefing (${mensagemDoErroDeCarga(e)}): o que está na tela é da última leitura.`);
    } finally { setLoading(false); }
  }, [leadId, leadData?.tradeName, leadData?.companyName]);

  useEffect(() => { load(); }, [load]);

  const set = (bloco: BriefingBloco, campo: string, valor: any) => {
    setBlocos(prev => {
      const next = { ...prev, [bloco]: { ...prev[bloco], [campo]: valor } };
      blocosRef.current = next;
      return next;
    });
    dirtyRef.current = true;
    setDirty(true);
    agendarAutosave();
  };

  // O motivo da última gravação que falhou: o flush o devolve ao pai, que segura a saída (ver registerFlush).
  const ultimoErroDeGravacaoRef = useRef<string | null>(null);
  /** Grava o que estiver em `blocosRef`. Devolve true quando gravou. */
  const gravar = useCallback(async (): Promise<boolean> => {
    if (!canEdit || !dirtyRef.current) return false;
    setSaving(true); setErro(null);
    try {
      const b = await briefingsService.save(leadId, blocosRef.current);
      setBriefing(b);
      dirtyRef.current = false;
      setDirty(false);
      onSavedRef.current?.(b);
      return true;
    } catch (e: any) {
      const m = e?.response?.data?.message;
      setErro(Array.isArray(m) ? m.join(' · ') : (m || 'Erro ao salvar o briefing'));
      ultimoErroDeGravacaoRef.current = mensagemDoErroDeCarga(e);
      return false;
    } finally { setSaving(false); }
  }, [canEdit, leadId]);

  /** Salva sozinho pouco depois da última tecla: o dado não depende de qual botão foi clicado. */
  const agendarAutosave = useCallback(() => {
    if (autosaveRef.current) window.clearTimeout(autosaveRef.current);
    autosaveRef.current = window.setTimeout(() => { gravar(); }, 1500);
  }, [gravar]);

  const salvar = async () => {
    if (autosaveRef.current) window.clearTimeout(autosaveRef.current);
    await gravar();
  };

  // O pai (modal do lead) chama isto antes de salvar o lead e fechar.
  useEffect(() => {
    if (!registerFlush) return;
    // ⛔ O flush LANÇA quando o texto não foi gravado (08/10/2026). Antes ele engolia a falha, o pai fechava o modal (ou
    // trocava de etapa, ou mandava gerar) e o editor desmontava: o aviso de erro sumia junto, e o texto se perdia
    // calado. Agora o pai sabe, e segura a saída (ProposalGenerationModal → sairDoBriefing).
    registerFlush(async () => {
      if (autosaveRef.current) window.clearTimeout(autosaveRef.current);
      await gravar();
      if (canEdit && dirtyRef.current) {
        throw new Error(ultimoErroDeGravacaoRef.current || 'o briefing não foi salvo');
      }
    });
    return () => registerFlush(null);
  }, [registerFlush, gravar, canEdit]);

  // Sair da aba ou fechar o modal com algo pendente: grava antes de desmontar.
  // É o ÚLTIMO recurso, para o desmonte que não passa pelo modal (o pai sai de cena de repente): aí não há mais tela
  // para dizer nada. Os caminhos do modal (fechar, trocar de etapa, gerar) chamam o flush ANTES e seguram a saída.
  useEffect(() => () => {
    if (autosaveRef.current) window.clearTimeout(autosaveRef.current);
    if (dirtyRef.current) briefingsService.save(leadId, blocosRef.current).catch(falhaIgnoradaDeProposito('gravar ao desmontar é o último recurso: não há mais tela para avisar, e os caminhos do modal descarregam antes e seguram a saída'));
  }, [leadId]);

  // Abrir uma etapa leva o cabeçalho dela para o topo; sem isto a etapa 2 abria
  // com a tela no meio do formulário.
  useEffect(() => {
    if (!jaAbriuRef.current) { jaAbriuRef.current = true; return; }
    const el = secoesRef.current[aberta];
    if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [aberta]);

  // Pendências vêm do backend a cada salvamento; entre um e outro, o que a tela
  // mostra é o último veredito, para o vendedor saber o que ainda falta.
  const pend = useMemo(() => {
    const map = new Map<string, BriefingPendencia>();
    for (const p of briefing?.pendencias || []) map.set(`${p.bloco}.${p.campo}`, p);
    return map;
  }, [briefing]);
  const isPend = (bloco: BriefingBloco, campo: string) => pend.has(`${bloco}.${campo}`);
  const cls = (bloco: BriefingBloco, campo: string) => `${INPUT} ${campoComEstado(isPend(bloco, campo) && PEND)}`;
  const pendPorEtapa = (etapa: number) => (briefing?.pendencias || []).filter(p => p.etapa === etapa);

  const porta = blocos.oferta.porta as string | undefined;
  const temWhatsapp = porta === 'whatsapp_first' || porta === 'combo';
  const temVoz = porta === 'voz_first' || porta === 'combo';

  if (loading) return <div className='text-sm text-slate-400 py-8 text-center'>Carregando briefing…</div>;
  if (!carregou) {
    return (
      <div role='alert' className='my-4 px-4 py-4 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-300' data-briefing='nao-carregou'>
        <div className='font-semibold'>{erro || 'Não consegui carregar o briefing.'}</div>
        <div className='mt-1 text-xs text-red-200/70'>
          Isto não quer dizer que o briefing está vazio: a leitura não chegou. O formulário fica fechado até ela dar certo,
          porque salvar sobre um formulário vazio apagaria o briefing que existe.
        </div>
        <button type='button' onClick={load} className='mt-3 px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-xs font-medium'>Tentar de novo</button>
      </div>
    );
  }

  // Helpers são FUNÇÕES, não componentes: componente definido dentro do render
  // ganha identidade nova a cada tecla, remonta o input e perde o foco.
  const field = (p: { bloco: BriefingBloco; campo: string; label: string; hint?: string; textarea?: boolean; type?: string; placeholder?: string }) => (
    <FieldView key={`${p.bloco}.${p.campo}`} label={p.label} hint={p.hint} textarea={p.textarea} type={p.type} placeholder={p.placeholder}
      exemplos={BRIEFING_EXEMPLOS[`${p.bloco}.${p.campo}`]}
      value={blocos[p.bloco][p.campo]} onChange={v => set(p.bloco, p.campo, v)} pend={isPend(p.bloco, p.campo)} disabled={!canEdit} />
  );
  const tri = (p: { bloco: BriefingBloco; campo: string; label: string; hint?: string }) => (
    <TriView key={`${p.bloco}.${p.campo}`} label={p.label} hint={p.hint} value={blocos[p.bloco][p.campo]}
      onChange={v => set(p.bloco, p.campo, v)} pend={isPend(p.bloco, p.campo)} disabled={!canEdit} />
  );
  const etapaProps = (n: 1 | 2 | 3) => ({
    n, aberta: aberta === n, onOpen: () => setAberta(n),
    innerRef: (el: HTMLElement | null) => { secoesRef.current[n] = el; },
    pendentes: n < 3 && briefing ? pendPorEtapa(n).length : null,
    completa: n === 1 ? briefing?.etapa1Completa : n === 2 ? briefing?.etapa2Completa : undefined,
  });

  return (
    <div className='space-y-4'>
      {/* Cabeçalho: uma ação primária, o veredito ao lado */}
      <div className='flex items-start justify-between gap-4 flex-wrap'>
        <div>
          <h3 className='text-base font-semibold text-white'>Briefing da proposta</h3>
          <p className='text-xs text-slate-500 mt-0.5'>O que não souber, deixe em branco. Campo vazio vira "a confirmar"; chute vira mentira impressa. <b className='text-slate-400'>Salva sozinho</b> enquanto você preenche.</p>
        </div>
        <div className='flex items-center gap-3'>
          {briefing && (
            briefing.prontoParaGerar
              ? <span className='text-xs px-2.5 py-1 rounded-full bg-green-500/15 text-green-400 border border-green-500/40'>Pronto para gerar</span>
              : <span className='text-xs px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/40'>{briefing.pendencias.filter(p => p.etapa < 3).length} pendência(s) travam a geração</span>
          )}
          {canEdit && (
            <button type='button' onClick={salvar} disabled={saving || !dirty} title='O briefing salva sozinho pouco depois da última alteração'
              className='px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white text-sm font-medium rounded-lg'>
              {saving ? 'Salvando…' : dirty ? 'Salvar agora' : 'Salvo ✓'}
            </button>
          )}
        </div>
      </div>

      {erro && <div className='text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2'>{erro}</div>}

      {briefing && briefing.pendencias.filter(p => p.etapa < 3).length > 0 && (
        <div className='bg-slate-800/50 border border-amber-500/30 rounded-xl p-3'>
          <p className='text-[11px] font-mono uppercase tracking-wide text-amber-400 mb-1.5'>Pendências</p>
          <ul className='grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5'>
            {briefing.pendencias.filter(p => p.etapa < 3).map(p => (
              <li key={`${p.bloco}.${p.campo}`} className='text-xs text-slate-300 flex items-center gap-2'>
                <span className='text-slate-500 font-mono'>E{p.etapa}</span>
                <button type='button' className='hover:text-white text-left' onClick={() => setAberta(p.etapa)}>{p.rotulo}</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <EtapaView {...etapaProps(1)} titulo='Descoberta comercial' sub='Vendedor, 5 minutos, logo depois da conversa. É o ouro da proposta.'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
          {field({ bloco: 'descoberta', campo: 'nomeFantasia', label: 'Nome fantasia' })}
          {field({ bloco: 'descoberta', campo: 'cidadeUf', label: 'Cidade/UF' })}
          {field({ bloco: 'descoberta', campo: 'site', label: 'Site ou Instagram' })}
        </div>
        {field({ bloco: 'descoberta', campo: 'oQueFaz', label: 'O que a empresa faz (1 frase)' })}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
          {field({ bloco: 'descoberta', campo: 'contatoNome', label: 'Com quem você falou' })}
          {field({ bloco: 'descoberta', campo: 'contatoCargo', label: 'Cargo' })}
          {field({ bloco: 'descoberta', campo: 'contatoWhatsapp', label: 'WhatsApp do contato' })}
        </div>
        {field({ bloco: 'descoberta', campo: 'dorFrase', label: 'A frase do cliente sobre o problema', textarea: true, placeholder: 'Escreva como o cliente disse, entre aspas', hint: 'Verbatim. É citada na proposta e ancora a recomendação inteira.' })}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          {field({ bloco: 'descoberta', campo: 'usamHoje', label: 'O que usam hoje', textarea: true, placeholder: 'Sistema, fornecedor, WhatsApp de que tipo' })}
          {field({ bloco: 'descoberta', campo: 'incidente', label: 'Já tiveram incidente? Como ele contou', textarea: true, placeholder: 'Bloqueio, queda, prejuízo' })}
        </div>
        {field({ bloco: 'descoberta', campo: 'numerosDitos', label: 'Números que O CLIENTE falou', textarea: true, placeholder: 'Mensagens/mês, ligações/dia, atendentes, unidades', hint: 'Só o que foi dito, sem interpretar. Dado vago nunca vira número específico na proposta.' })}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          {field({ bloco: 'descoberta', campo: 'decisores', label: 'Quem mais decide ou influencia', placeholder: 'Sócio, TI, financeiro' })}
          {field({ bloco: 'descoberta', campo: 'concorrente', label: 'Concorrente na jogada' })}
          {field({ bloco: 'descoberta', campo: 'preocupacoes', label: 'Preocupações que apareceram', textarea: true, placeholder: 'Migração, preço, prazo, equipe não técnica' })}
          {field({ bloco: 'descoberta', campo: 'valoriza', label: 'O que ele mais valoriza', textarea: true, placeholder: 'Não parar, economizar, imagem, prazo legal' })}
        </div>
      </EtapaView>

      <EtapaView {...etapaProps(2)} titulo='Técnica e comercial' sub='Oferta, dimensionamento, provas autorizadas e condições. Com apoio, antes de gerar.'>
        <div>
          <label className='block text-xs text-slate-400 mb-1'>
            Porta de entrada
            {isPend('oferta', 'porta') && <span className='ml-2 text-[10px] font-mono uppercase tracking-wide text-amber-400'>a confirmar</span>}
          </label>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-2'>
            {PORTAS.map(p => (
              <button key={p.v} type='button' disabled={!canEdit} onClick={() => set('oferta', 'porta', p.v)}
                className={`text-left px-3 py-2 rounded-lg border transition-colors ${porta === p.v ? 'bg-orange-500/10 border-orange-500/60 ring-1 ring-orange-500/40' : 'bg-slate-800 border-slate-700 hover:border-slate-500'}`}>
                <p className={`text-sm font-medium ${porta === p.v ? 'text-orange-300' : 'text-white'}`}>{p.l}</p>
                <p className='text-[11px] text-slate-500 leading-snug'>{p.d}</p>
              </button>
            ))}
          </div>
          <p className='text-[11px] text-slate-500 mt-1'>A proposta escolhe uma porta pela dor da descoberta e recomenda com firmeza. Nunca "você escolhe".</p>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          <div>
            <label className='block text-xs text-slate-400 mb-1'>Variante{isPend('oferta', 'variante') && <span className='ml-2 text-[10px] font-mono uppercase tracking-wide text-amber-400'>a confirmar</span>}</label>
            <select value={blocos.oferta.variante ?? ''} disabled={!canEdit} onChange={e => set('oferta', 'variante', e.target.value || undefined)} className={cls('oferta', 'variante')}>
              <option value=''>Selecione…</option>
              <option value='completa'>Completa (9 páginas): missão crítica, ticket alto, setor público</option>
              <option value='enxuta'>Enxuta (6 páginas): SMB simples, etapa inicial</option>
            </select>
          </div>
          {porta === 'setor_publico' && field({ bloco: 'oferta', campo: 'setorPublicoEdital', label: 'Edital / modalidade' })}
        </div>
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3'>
          {temWhatsapp && field({ bloco: 'oferta', campo: 'usuariosCombo', label: 'Usuários (Combo)', type: 'number', hint: 'mín. 10' })}
          {temVoz && field({ bloco: 'oferta', campo: 'ramaisVoz', label: 'Ramais de voz', type: 'number' })}
          {temVoz && field({ bloco: 'oferta', campo: 'ramaisAvulsos', label: 'Ramais avulsos', type: 'number', hint: 'portaria, salas' })}
          {(temVoz || temWhatsapp) && field({ bloco: 'oferta', campo: 'canais', label: 'Canais de voz', type: 'number', hint: 'mín. 5; 0 = sem PSTN' })}
          {temWhatsapp && field({ bloco: 'oferta', campo: 'numerosWhatsappAdicionais', label: 'WhatsApp adicionais', type: 'number', hint: '1º incluso' })}
          {temVoz && field({ bloco: 'oferta', campo: 'didsAdicionais', label: 'Números diretos (DID)', type: 'number', hint: '1 por pacote incluso' })}
        </div>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-3 items-start'>
          {tri({ bloco: 'oferta', campo: 'precoTabela', label: 'Preço da tabela vigente?', hint: 'Fora da tabela exige aprovador nomeado.' })}
          {blocos.oferta.precoTabela === false && field({ bloco: 'oferta', campo: 'precoNegociadoValor', label: 'Valor negociado (R$/mês)', type: 'number' })}
          {blocos.oferta.precoTabela === false && field({ bloco: 'oferta', campo: 'precoNegociadoAprovador', label: 'Quem aprovou' })}
        </div>
        {field({ bloco: 'oferta', campo: 'pacoteRecomendado', label: 'Pacote a RECOMENDAR (um só) e por quê', textarea: true })}

        <div className='pt-2 border-t border-slate-700/60'>
          <p className='text-[11px] font-mono uppercase tracking-wide text-slate-500 mb-3'>Dimensionamento{!porta && ' · escolha a porta para ver o bloco'}</p>
          {temWhatsapp && (
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mb-3'>
              {field({ bloco: 'dimensionamento', campo: 'waNumeroAtualDeQuem', label: 'O número atual de WhatsApp é de quem', placeholder: 'Empresa ou pessoa' })}
              {tri({ bloco: 'dimensionamento', campo: 'waTemBusinessManager', label: 'Já tem conta comercial / Business Manager na Meta?' })}
              {field({ bloco: 'dimensionamento', campo: 'waVolumeMensagensMes', label: 'Volume estimado de mensagens iniciadas/mês', placeholder: 'Com a vagueza do cliente: "cerca de"' })}
              {field({ bloco: 'dimensionamento', campo: 'waQuemDispara', label: 'Quem dispara', placeholder: 'Equipe ou sistema' })}
            </div>
          )}
          {temVoz && (
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              {field({ bloco: 'dimensionamento', campo: 'vozChamadasSimultaneasPico', label: 'Chamadas simultâneas no pico (estimativa)' })}
              {field({ bloco: 'dimensionamento', campo: 'vozProvedorAtual', label: 'Provedor atual' })}
              {field({ bloco: 'dimensionamento', campo: 'vozNumerosPortar', label: 'Números / 0800 para portar', placeholder: 'Quantos e quais; o do pacote não conta' })}
              {field({ bloco: 'dimensionamento', campo: 'vozPabxFisicoContrato', label: 'PABX físico ou contrato vigente com multa?' })}
              {field({ bloco: 'dimensionamento', campo: 'vozRamaisSemUsuarioOnde', label: 'Ramais sem usuário: onde e quantos', placeholder: 'Portaria, recepção, salas, equipamentos' })}
            </div>
          )}
        </div>

        <div className='pt-2 border-t border-slate-700/60'>
          <p className='text-[11px] font-mono uppercase tracking-wide text-slate-500 mb-1'>Provas autorizadas para ESTA peça</p>
          <p className='text-[11px] text-slate-500 mb-3'>Marque só o confirmado. Cliente não autorizado não entra na proposta nem como rodapé. Nada aqui é obrigatório para gerar.</p>
          <p className='text-xs text-slate-400 mb-2'><b className='text-slate-200'>1. Provas institucionais.</b> Quais clientes da IPCOM esta proposta pode citar. Marque pelo encaixe: SAMU e Marinha provam missão crítica, Sicoob e Scania provam porte, Oftalmoclínica é espelho de quem vive de agendar (só como cliente de telefonia).</p>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-2 mb-3'>
            {[['institucional', 'SAMU / Marinha'], ['sicoob', 'Sicoob'], ['scania', 'Scania'], ['oftalmoclinica', 'Oftalmoclínica (telefonia)']].map(([k, l]) => (
              <label key={k} className='flex items-center gap-2 text-sm text-slate-300 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2'>
                <input type='checkbox' disabled={!canEdit} checked={!!blocos.provas[k]} onChange={e => set('provas', k, e.target.checked)} className='w-4 h-4 rounded bg-slate-800 border-slate-600' />{l}
              </label>
            ))}
          </div>
          <p className='text-xs text-slate-400 mb-2 pt-2 border-t border-slate-700/40'><b className='text-slate-200'>2. Case-espelho.</b> Um cliente parecido, do mesmo segmento ou da mesma cidade. As caixas abaixo dizem o que <b className='text-slate-200'>esse cliente</b> autorizou: o Grupo Vigivel, por exemplo, liberou o nome mas não o logo nem o depoimento. Sem case-espelho autorizado, deixe tudo em branco.</p>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
            {field({ bloco: 'provas', campo: 'caseEspelho', label: 'Case-espelho: qual cliente', placeholder: 'Ex.: Grupo Vigivel Segurança' })}
            {field({ bloco: 'provas', campo: 'caseEspelhoUsa', label: 'O que ele REALMENTE usa', placeholder: 'Ex.: telefonia com integração ao Sigma Cloud' })}
          </div>
          <p className='text-[11px] text-slate-500 mt-3 mb-1'>O case-espelho autorizou o uso de:</p>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-2'>
            {[['autorizaNome', 'citar o nome'], ['autorizaLogo', 'usar o logo'], ['autorizaDepoimento', 'citar depoimento'], ['autorizaConversa', 'falar com o prospect']].map(([k, l]) => (
              <label key={k} className='flex items-center gap-2 text-xs text-slate-300 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2'>
                <input type='checkbox' disabled={!canEdit} checked={!!blocos.provas[k]} onChange={e => set('provas', k, e.target.checked)} className='w-4 h-4 rounded bg-slate-800 border-slate-600' />{l}
              </label>
            ))}
          </div>
        </div>

        <div className='pt-2 border-t border-slate-700/60'>
          <p className='text-[11px] font-mono uppercase tracking-wide text-slate-500 mb-3'>Condições</p>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
            {field({ bloco: 'condicoes', campo: 'validadeDias', label: 'Validade da proposta (dias)', type: 'number' })}
            {field({ bloco: 'condicoes', campo: 'responsavelNome', label: 'Responsável comercial (IPCOM)', placeholder: 'Quem assina a proposta', hint: 'Sai na última página, no bloco de próximo passo. Não é o contato do cliente.' })}
            {field({ bloco: 'condicoes', campo: 'responsavelWhatsapp', label: 'WhatsApp do responsável (IPCOM)', placeholder: '(45) 3122-5150' })}
          </div>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 items-start'>
            {tri({ bloco: 'condicoes', campo: 'semFidelidade', label: 'Sem fidelidade e sem multa confirmado?' })}
            {blocos.condicoes.semFidelidade === true && field({ bloco: 'condicoes', campo: 'semFidelidadeConfirmadoPor', label: 'Confirmado por quem' })}
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mt-3'>
            {field({ bloco: 'condicoes', campo: 'prazoImplantacao', label: 'Prazo de implantação', hint: 'Sem confirmação operacional: "definido no agendamento da virada". Nunca prazo herdado de outra proposta.' })}
            {blocos.condicoes.prazoImplantacao && field({ bloco: 'condicoes', campo: 'prazoConfirmadoPor', label: 'Prazo confirmado por quem' })}
          </div>
        </div>
      </EtapaView>

      {mostrarEtapa3 && (
        <EtapaView {...etapaProps(3)} titulo='Pós-envio' sub='Resultado e aprendizado. Padrão recorrente vira atualização do núcleo.'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
            {field({ bloco: 'resultado', campo: 'enviadaEm', label: 'Enviada em', type: 'date' })}
            <div>
              <label className='block text-xs text-slate-400 mb-1'>Resultado</label>
              <select value={blocos.resultado.resultado ?? ''} disabled={!canEdit} onChange={e => set('resultado', 'resultado', e.target.value || undefined)} className={`${INPUT} ${campoComEstado()}`}>
                <option value=''>Em aberto</option>
                <option value='fechou'>Fechou</option>
                <option value='perdeu'>Perdeu</option>
                <option value='em_aberto'>Em aberto</option>
              </select>
            </div>
          </div>
          {field({ bloco: 'resultado', campo: 'objecao', label: 'Objeção que apareceu', textarea: true })}
          {field({ bloco: 'resultado', campo: 'aprendizado', label: 'O que aprendemos (vira melhoria no núcleo?)', textarea: true })}
        </EtapaView>
      )}
    </div>
  );
}
